import React, { useState, useEffect, useCallback } from 'react';
import { apiCall, withToken, getApiBaseUrl } from '../config';
import { notify } from '../ui/notify';

// Schadens- und Verlustmeldungen (Formular auf gepaeckannahme.de/schaden). Nur Admin.
const STATUS = {
  new: { label: 'neu', cls: 'bg-danger' },
  review: { label: 'in Prüfung', cls: 'bg-warning text-dark' },
  settled: { label: 'reguliert', cls: 'bg-success' },
  rejected: { label: 'abgelehnt', cls: 'bg-secondary' },
};
const ITEM = { rucksack: 'Rucksack', tasche: 'Tasche', kinderwagen: 'Kinderwagen', koffer: 'Koffer', sonstiges: 'Sonstiges' };
const ibanPretty = (i) => String(i || '').replace(/(.{4})/g, '$1 ').trim();
const dateDE = (iso) => (iso && iso.includes('-') ? iso.slice(0, 10).split('-').reverse().join('.') : iso || '–');
const photoUrl = (file) => withToken(`${getApiBaseUrl()}/luggage-claim-photos/${file}`);

const LuggageClaimsAdmin = () => {
  const [data, setData] = useState({ claims: [], maxPayout: 100, lateLink: '' });
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState({});
  const [zoom, setZoom] = useState(null);

  const load = useCallback(async () => {
    try { setData(await apiCall('/luggage-claims')); } catch (e) { notify('Schadensmeldungen konnten nicht geladen werden: ' + e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (c, patch) => {
    try {
      await apiCall(`/luggage-claims/${c.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      load();
    } catch (e) { notify(e.message || 'Speichern fehlgeschlagen'); }
  };

  const openCount = data.claims.filter((c) => c.status === 'new' || c.status === 'review').length;
  const lateUrl = data.lateLink ? `${window.location.origin}${data.lateLink}` : '';

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h4 className="mb-0 me-auto">🧳⚠️ Schadens- &amp; Verlustmeldungen {openCount > 0 && <span className="badge bg-danger ms-1">{openCount} offen</span>}</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={load}>Aktualisieren</button>
      </div>
      <div className="alert alert-light border small py-2">
        Kundenlink: <code>{window.location.origin}/schaden</code> – Meldung nur am selben Tag, Foto der Gepäckmarke Pflicht, Regulierung bis {data.maxPayout} € (AGB).
        {lateUrl && <> Für verspätete Fälle gibt es einen Ausnahme-Link, den nur du weitergibst:{' '}
          <button className="btn btn-link btn-sm p-0 align-baseline" onClick={() => { navigator.clipboard.writeText(lateUrl); notify('Ausnahme-Link kopiert'); }}>Ausnahme-Link kopieren</button></>}
      </div>

      {!data.claims.length && <div className="text-muted py-4 text-center">Noch keine Meldungen.</div>}
      {data.claims.map((c) => {
        const isOpen = open === c.id;
        const e = edit[c.id] || {};
        return (
          <div key={c.id} className="card mb-2">
            <div className="card-header d-flex flex-wrap align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : c.id)}>
              <strong>#S{c.id}</strong>
              <span className={`badge ${c.type === 'lost' ? 'bg-dark' : 'bg-info text-dark'}`}>{c.type === 'lost' ? 'nicht auffindbar' : 'beschädigt'}</span>
              <span>{c.item_type ? `${ITEM[c.item_type] || c.item_type} · ` : ''}Marke <strong>{c.tag_number}</strong>{c.station ? ` · ${c.station}` : ''}</span>
              <span className="text-muted">{c.customer_name}</span>
              {!c.reported_at_station && <span className="badge bg-light text-danger border">nicht an Station gemeldet</span>}
              {!!c.late && <span className="badge bg-light text-dark border">Ausnahme-Link</span>}
              <span className={`badge ms-auto ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label}</span>
              <span className="text-muted small">{dateDE(c.created_at)}</span>
            </div>
            {isOpen && (
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div><strong>Kunde:</strong> {c.customer_name}</div>
                    <div><strong>Telefon:</strong> <a href={`tel:${c.customer_phone}`}>{c.customer_phone}</a></div>
                    <div><strong>E-Mail:</strong> <a href={`mailto:${c.customer_email}?subject=Ihre Meldung %23S${c.id}`}>{c.customer_email}</a></div>
                    <div className="mt-2"><strong>Abgabe:</strong> {dateDE(c.dropoff_date)} · <strong>{c.type === 'lost' ? 'fehlt seit' : 'Abholung'}:</strong> {dateDE(c.event_date)}</div>
                    <div className="mt-2"><strong>Gepäckstück:</strong> {c.item_type ? `${ITEM[c.item_type] || c.item_type} – ` : ''}{c.item_description}</div>
                    {c.damage_description && <div className="mt-1"><strong>Schaden:</strong> {c.damage_description}</div>}
                    {c.contents && <div className="mt-1"><strong>Inhalt:</strong> {c.contents}</div>}
                    {c.estimated_value != null && <div className="mt-1"><strong>Geschätzter Zeitwert:</strong> {c.estimated_value} €</div>}
                    <div className="mt-2 p-2 bg-light rounded">
                      <strong>Bankverbindung:</strong>{' '}
                      {c.iban ? <>
                        <code>{ibanPretty(c.iban)}</code>{' '}
                        <button className="btn btn-link btn-sm p-0 align-baseline" onClick={() => { navigator.clipboard.writeText(c.iban); notify('IBAN kopiert'); }}>kopieren</button>
                        <div className="small">Kontoinhaber: {c.account_holder || '–'}</div>
                      </> : <span className="text-muted">{c.no_iban ? 'kein IBAN-Konto (Kunde außerhalb Europas?) – Auszahlung individuell klären' : 'nicht angegeben'}</span>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex flex-wrap gap-2">
                      {[{ f: c.tag_photo, l: 'Gepäckmarke' }, ...c.photos.map((f, i) => ({ f, l: `Foto ${i + 1}` })), ...(c.receipt_photo ? [{ f: c.receipt_photo, l: 'Kaufbeleg' }] : [])].map(({ f, l }) => (
                        /\.pdf$/i.test(f)
                          ? <a key={f} href={photoUrl(f)} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">📄 {l}</a>
                          : <figure key={f} className="m-0 text-center" style={{ cursor: 'zoom-in' }} onClick={() => setZoom(photoUrl(f))}>
                              <img src={photoUrl(f)} alt={l} style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                              <figcaption className="small text-muted">{l}</figcaption>
                            </figure>
                      ))}
                    </div>
                  </div>
                </div>
                <hr />
                <div className="row g-2 align-items-end">
                  <div className="col-md-3"><label className="form-label small mb-0">Status</label>
                    <select className="form-select form-select-sm" value={e.status ?? c.status} onChange={(ev) => setEdit({ ...edit, [c.id]: { ...e, status: ev.target.value } })}>
                      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select></div>
                  <div className="col-md-2"><label className="form-label small mb-0">Auszahlung € (max. {data.maxPayout})</label>
                    <input className="form-control form-control-sm" inputMode="decimal" value={e.payout_amount ?? (c.payout_amount ?? '')} onChange={(ev) => setEdit({ ...edit, [c.id]: { ...e, payout_amount: ev.target.value } })} /></div>
                  <div className="col-md-5"><label className="form-label small mb-0">Interne Notiz</label>
                    <input className="form-control form-control-sm" value={e.admin_note ?? (c.admin_note || '')} onChange={(ev) => setEdit({ ...edit, [c.id]: { ...e, admin_note: ev.target.value } })} /></div>
                  <div className="col-md-2"><button className="btn btn-primary btn-sm w-100" onClick={() => save(c, { status: e.status ?? c.status, payout_amount: e.payout_amount ?? c.payout_amount ?? '', admin_note: e.admin_note ?? c.admin_note ?? '' })}>Speichern</button></div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
          <img src={zoom} alt="" style={{ maxWidth: '96vw', maxHeight: '96vh' }} />
        </div>
      )}
    </div>
  );
};

export default LuggageClaimsAdmin;
